import { afterNextRender, Component, inject, Injector, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { downloadBlob } from "../../utils/blob-viewer.util";
import { CommonModule } from "@angular/common";
import { MatSelectModule } from "@angular/material/select";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatCardModule } from "@angular/material/card";
import { MatTabsModule } from "@angular/material/tabs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { FormsModule, ReactiveFormsModule, FormControl } from "@angular/forms";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";

import { MipsGaugesComponent } from "@components/mipsgauage/mips-gauges.component";
import { sampleGaugesProd } from "@components/mipsgauage/gauage-mockdata";
import { getDashboardPerformanceYears, getCurrentPerformanceYear } from "../../constants/mips-constants";
import { DashboardMipsScoreBlock, DashboardTopMeasure } from "@models/dashboard-mips-scores.model";
import { CalculateMipsReweightingRequest, CalculateMipsReweightingResponse } from "@models/mips-reweighting-score.model";
import { buildQualityAttestationInput } from "@services/mips-reweighting.service";

import { DashboardProviderProd } from "@models/dashboard-providers.model";
import { DashboardMeasureDetailProd } from "@models/dashboard-measure-details.model";
import { DashboardMeasurePatient } from "@models/dashboard-measure-patient.model";
import { GaugeData } from "@models/gauges.model";
import { DashboardService } from "@services/dashboard.service";
import { MeasureDetailsProdComponent } from "@components/measuredetailsprod/measure-details.component";
import { PatientMeasureDetailsProdComponent } from "@components/measurepatientdetailsprod/patient-measure-details.component";
import {
  Subject,
  Subscription,
  combineLatest,
  Observable,
  BehaviorSubject,
} from "rxjs";
import { map, startWith, filter, take } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { selectOrganizationID } from "@store/selectors/add-organization/add-organization.selectors";
import { selectActiveUser } from "@store/selectors/user/user.selectors";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatDividerModule } from "@angular/material/divider";
import { DashboardMeasureLocation } from "@models/dashboard-location.model";
import { DashboardFilterRequest } from "@models/dashboard-filter.model";
import { MeasureMonthlyTrendComponent } from "@components/measure-monthly-trend/measure-monthly-trend.component";
import { DashboardActions } from "@store/actions/dashboard/dashboard.actions";
import { MipsQuestionnaireActions } from "@store/actions/mips-questionnaire/mips-questionnaire.actions";

import {
  selectProviders,
  selectLocations,
  selectSortedProviders,
  selectDetailsTab,
  selectIndex,
  selectShowMeasureDetails,
  selectMeasures,
  selectFilteredMeasures,
  selectFavoriteMeasureCodes,
  selectIsMultiOrgTin,
  selectSavedFilters,
  selectDeciles,
  selectMipsReweighting,
  selectMipsReweightingLoading,
  selectMipsScoreBundle,
  selectProgramYear,
} from "@store/selectors/dashboard/dashboard.selectors";
import {
  selectEffectiveQuestionnaire,
} from "@store/selectors/mips-packet/mips-packet.selector";

@Component({
  selector: "app-dashboard",
  standalone: true,
  imports: [
    MatIconModule,
    MatSlideToggleModule,
    MatCardModule,
    CommonModule,
    MatSelectModule,
    MatButtonModule,
    MatTabsModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MeasureDetailsProdComponent,
    PatientMeasureDetailsProdComponent,
    MipsGaugesComponent,
    MatCheckboxModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MeasureMonthlyTrendComponent,
  ],
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.css"],
})
export class DashboardProdComponent implements OnInit, OnDestroy {
  @ViewChild(PatientMeasureDetailsProdComponent)
  patientDetailSection!: PatientMeasureDetailsProdComponent;

  showBenchmarks = true;
  selectedTab = "all";
  selectedDetailsTab = "performanceData";
  showMeasureDetails = false;
  showPatientDetails = false;
  selectedIndex: number | null = null;
  isSmallPractice = false;

  private readonly store = inject(Store);
  private readonly injector = inject(Injector);
  private readonly _destroying$ = new Subject<void>();

  private readonly reweightingTogglesSubject = new BehaviorSubject<{ quality: boolean; pi: boolean; ia: boolean }>({
    quality: false,
    pi: false,
    ia: false,
  });

  readonly measureSearchControl = new FormControl<string>("");
  readonly clinicianSearchControl = new FormControl<string>("");
  readonly locationSearchControl = new FormControl<string>("");

  measures: DashboardMeasureDetailProd[] = [];
  uniqueMeasures: any[] = [];
  gauges: GaugeData[] = [];
  providers: DashboardProviderProd[] = [];
  locations: DashboardMeasureLocation[] = [];
  isLoading = false;
  years = getDashboardPerformanceYears().map(String);

  private readonly uniqueMeasuresSubject = new BehaviorSubject<any[]>([]);

  readonly filteredUniqueMeasures$: Observable<any[]>;
  readonly filteredProviders$: Observable<DashboardProviderProd[]>;
  readonly filteredLocations$: Observable<DashboardMeasureLocation[]>;

  organizationID$ = this.store.select(selectOrganizationID);
  activeUser$ = this.store.select(selectActiveUser);

  selectedIndex$ = this.store.select(selectIndex);
  showMeasureDetails$ = this.store.select(selectShowMeasureDetails);
  measures$ = this.store.select(selectMeasures);
  filteredMeasures$ = this.store.select(selectFilteredMeasures);
  selectedDetailsTab$ = this.store.select(selectDetailsTab);
  providers$ = this.store.select(selectSortedProviders);
  locations$ = this.store.select(selectLocations);
  mipsScoreBundle$ = this.store.select(selectMipsScoreBundle);
  isMultiOrgTin$ = this.store.select(selectIsMultiOrgTin);
  readonly deciles$ = this.store.select(selectDeciles);
  readonly reweighting$ = this.store.select(selectMipsReweighting);
  readonly reweightingLoading$ = this.store.select(selectMipsReweightingLoading);

  private latestMipsScores: DashboardMipsScoreBlock | null = null;
  private latestMvpScores: DashboardMipsScoreBlock | null = null;
  private latestTopTraditionalMeasures: DashboardTopMeasure[] = [];
  private latestTopMvpMeasures: DashboardTopMeasure[] = [];
  private latestReweighting: CalculateMipsReweightingResponse | null = null;

  isTinAggregationEnabled = false;
  isExportingPatients = false;
  filteredMeasures: DashboardMeasureDetailProd[] = [];
  measuresWithoutResults: DashboardMeasureDetailProd[] = [];
  isNoResultsSectionExpanded = false;
  isLegendExpanded = false;
  selectedMeasure: DashboardMeasureDetailProd | null = null;
  showFavoritesOnly = false;
  favoriteMeasureCodesSet = new Set<string>();

  readonly favoriteMeasureCodes$ = this.store.select(selectFavoriteMeasureCodes);

  private initSubscription = new Subscription();

  filters = {
    measureSelection: ["all"] as string[],
    clinician: ["all"] as string[],
    location: ["all"] as string[],
    selectGroup: "",
    measureYear: String(getCurrentPerformanceYear()),
  };

  constructor() {
    this.filteredUniqueMeasures$ = this.createFilteredMeasures();
    this.filteredProviders$ = this.createFilteredProviders();
    this.filteredLocations$ = this.createFilteredLocations();
  }

  ngOnInit(): void {
    this.isLoading = true;

    let isTinAggregationToRestore: boolean | null = null;
    this.store.select(selectSavedFilters).pipe(take(1)).subscribe((savedFilters) => {
      if (savedFilters) {
        this.filters = {
          measureSelection: savedFilters.measureSelection,
          clinician: savedFilters.clinician,
          location: savedFilters.location,
          selectGroup: savedFilters.selectGroup,
          measureYear: savedFilters.measureYear,
        };
        isTinAggregationToRestore = savedFilters.isTinAggregationEnabled;
      }
    });

    this.initSubscription.add(
      this.organizationID$
        .pipe(filter((orgId) => !!orgId && orgId !== ""))
        .subscribe(() => {
          this.isTinAggregationEnabled = isTinAggregationToRestore ?? false;
          isTinAggregationToRestore = null;
        }),
    );

    this.initSubscription.add(
      this.measures$.subscribe((measures) => {
        this.measures = measures;
        this.uniqueMeasures = this.getUniqueMeasuresById(measures.filter(m => m.hasResults !== false));
        this.uniqueMeasuresSubject.next(this.uniqueMeasures);
        this.applyTabFilter(this.selectedTab);

        if (this.filteredMeasures.length > 0) {
          this.selectedMeasure = this.filteredMeasures[0];
        }
        this.selectedIndex = null;
        this.isLoading = false;

      }),
    );

    this.initSubscription.add(
      this.providers$.subscribe((providers) => {
        this.providers = providers;
      }),
    );

    this.initSubscription.add(
      this.locations$.subscribe((locations) => {
        this.locations = locations;
      }),
    );

    this.initSubscription.add(
      this.favoriteMeasureCodes$.subscribe((codes) => {
        this.favoriteMeasureCodesSet = new Set(codes);
        this.applyTabFilter(this.selectedTab);
      }),
    );

    // Ensure the questionnaire is actually loaded when landing directly on the Dashboard —
    // selectEffectiveQuestionnaire only gets populated by visiting the MIPS wizard otherwise,
    // so without this, a bare dashboard visit never seeds the small-practice/reweighting toggles.
    this.initSubscription.add(
      combineLatest([this.organizationID$, this.store.select(selectProgramYear)]).pipe(
        filter(([orgId]) => !!orgId && orgId !== ''),
        take(1),
      ).subscribe(([orgId, programYear]) => {
        this.store.dispatch(MipsQuestionnaireActions.loadMIPSQuestionnaire({ orgId, performanceYear: programYear }));
      }),
    );

    // Wait for the questionnaire to load before reading initial toggle state.
    // take(1) alone fires immediately with a null questionnaire on fresh page load.
    this.initSubscription.add(
      this.store.select(selectEffectiveQuestionnaire).pipe(
        filter(q => q != null),
        take(1),
      ).subscribe((questionnaire) => {
        const quality = questionnaire?.appliedHardship === 'yes' && questionnaire.hardshipQuality === 'yes';
        const pi = questionnaire?.appliedHardship === 'yes' && questionnaire.hardshipPromotingInteroperability === 'yes';
        const ia = questionnaire?.appliedHardship === 'yes' && questionnaire.hardShipImprovementActivities === 'yes';
        this.reweightingTogglesSubject.next({ quality, pi, ia });
        // Just check the box — don't dispatch setSmallPractice (that's the manual override
        // path, bound to the checkbox's own (change) handler, and triggers a full reload). The
        // initial dashboard load already reflects small-practice scoring correctly on its own,
        // since the server falls back to the questionnaire whenever no override is provided.
        if (questionnaire?.specialStatusSmall === 'yes' && !this.isSmallPractice) {
          this.isSmallPractice = true;
        }
        this.refreshMipsReweighting();
      }),
    );

    this.initSubscription.add(
      this.mipsScoreBundle$.subscribe(({ mipsScores, mvpScores, topTraditionalMeasures, topMvpMeasures }) => {
        this.latestMipsScores = mipsScores;
        this.latestMvpScores = mvpScores;
        this.latestTopTraditionalMeasures = topTraditionalMeasures;
        this.latestTopMvpMeasures = topMvpMeasures;
        this.refreshMipsReweighting();
      }),
    );

    this.initSubscription.add(
      this.reweighting$.subscribe((reweighting) => {
        this.latestReweighting = reweighting;
        this.updateGaugesFromReweighting();
      }),
    );
  }

  ngOnDestroy(): void {
    this.initSubscription.unsubscribe();
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Dispatches a recalculation of category weights + weighted scores against the API's
   * MipsReweightingService, using the current toggle state and both tabs' raw totals in a
   * single call. Category weights only depend on the hardship toggles + small-practice flag,
   * not on which tab is active, so bundling both tracks means switching tabs never needs a
   * new call — only toggle/score/small-practice changes do.
   */
  private refreshMipsReweighting(): void {
    if (!this.latestMipsScores && !this.latestMvpScores) return;

    const toggles = this.reweightingTogglesSubject.value;
    const request: CalculateMipsReweightingRequest = {
      traditional: buildQualityAttestationInput(
        this.latestMipsScores?.qualityPoints ?? 0,
        this.latestMipsScores?.qualityTotal ?? 0,
        60,
        this.latestMipsScores?.iaTotal ?? null,
        this.latestMipsScores?.piTotal ?? null,
      ),
      mvp: buildQualityAttestationInput(
        this.latestMvpScores?.qualityPoints ?? 0,
        this.latestMvpScores?.qualityTotal ?? 0,
        40,
        this.latestMvpScores?.iaTotal ?? null,
        this.latestMvpScores?.piTotal ?? null,
      ),
      hardshipQuality: toggles.quality,
      hardshipPi: toggles.pi,
      hardshipIa: toggles.ia,
      hardshipCost: false,
      isSmallPractice: this.isSmallPractice,
    };

    this.store.dispatch(DashboardActions.calculateMipsReweighting({ request }));
  }

  private updateGaugesFromReweighting(): void {
    const isMvpTab = this.selectedTab.toLowerCase() === 'mvp measures';
    // Only the measures that actually count toward the quality score belong in the tooltip —
    // the API's top-measures list is top 10 (for the practice summary report), but quality
    // scoring only counts the top 6 (traditional) / top 4 (MVP) of those.
    const qualityMeasureCount = isMvpTab ? 4 : 6;
    const topMeasures = (isMvpTab ? this.latestTopMvpMeasures : this.latestTopTraditionalMeasures).slice(0, qualityMeasureCount);
    const reweighting = this.latestReweighting;

    if (!reweighting) return;

    const { weights } = reweighting;
    const scores = isMvpTab ? reweighting.mvp : reweighting.traditional;
    const toggles = this.reweightingTogglesSubject.value;
    const totalMax = weights.quality + weights.ia + weights.pi;

    this.gauges = [
      {
        id: 'quality', title: 'Quality', value: scores.qualityWeightedScore ?? 0, min: 0, max: weights.quality,
        goal: Math.round(weights.quality * 0.9), color: '#8e6b9f', type: 'semi',
        tooltip: this.buildQualityTooltip(topMeasures, qualityMeasureCount, scores.qualityTotal - scores.qualityPoints), isReweighted: toggles.quality, showReweightToggle: true,
      },
      {
        id: 'improvement', title: 'Improvement Activities', value: scores.iaWeightedScore ?? 0, min: 0, max: weights.ia,
        goal: Math.round(weights.ia * 14 / 15), color: '#0a9db7', type: 'semi',
        isReweighted: toggles.ia, showReweightToggle: true,
      },
      {
        id: 'interoperability', title: 'Promoting Interoperability', value: scores.piWeightedScore ?? 0, min: 0, max: weights.pi,
        goal: Math.round(weights.pi * 22 / 25), color: '#e3a008', type: 'semi',
        isReweighted: toggles.pi, showReweightToggle: true,
      },
      {
        id: 'total', title: 'Total Estimated Score', value: scores.estimatedFinalScore ?? 0, min: 0, max: totalMax,
        goal: Math.round(totalMax * 0.9), color: '#65a30d', type: 'semi',
      },
    ];
  }

  onGaugeReweightToggle(gaugeId: string): void {
    const current = this.reweightingTogglesSubject.value;
    const updated = { ...current };
    if (gaugeId === 'quality') updated.quality = !current.quality;
    else if (gaugeId === 'improvement') updated.ia = !current.ia;
    else if (gaugeId === 'interoperability') updated.pi = !current.pi;
    else return;
    this.reweightingTogglesSubject.next(updated);
    this.refreshMipsReweighting();
  }

  private buildQualityTooltip(topMeasures: DashboardTopMeasure[], qualityMeasureCount: number, smallPracticeBonus: number): string {
    let tooltip = `Top ${qualityMeasureCount} Measures:\n`;
    topMeasures.forEach((measure) => {
      const measureId = measure.measureIdTitle.split(': ')[0].trim();
      tooltip += `${measureId}: ${measure.mipsEstimatedScore}\n`;
    });
    if (smallPracticeBonus > 0) {
      tooltip += `\nSmall Practice Bonus: +${smallPracticeBonus}`;
    }
    return tooltip;
  }

  getGaugeTitleName(measure: DashboardMeasureDetailProd): string {
    if (measure.measureDisplayName && measure.measureDisplayName.length > 0) {
      return measure.measureDisplayName;
    } else if (measure.name) {
      if (measure.isMultiPerformance) {
        const cleanName = measure.name.replace(/-\d+$/, "");
        const performanceType = measure.performanceType;
        const nthIndex = parseInt(performanceType ?? '', 10);
        if (!isNaN(nthIndex)) {
          const suffix = nthIndex === 1 ? 'st' : nthIndex === 2 ? 'nd' : nthIndex === 3 ? 'rd' : 'th';
          return `${cleanName} (${nthIndex}${suffix} Performance Rate)`;
        }
        return cleanName;
      }

      return measure.name;
    } else {
      return "";
    }
  }

  isFavorite(measureCode: string | null | undefined): boolean {
    if (!measureCode) return false;
    return this.favoriteMeasureCodesSet.has(measureCode);
  }

  toggleFavorite(measure: DashboardMeasureDetailProd, event: Event): void {
    event.stopPropagation();
    const measureCode = measure.name ?? '';
    this.activeUser$.pipe(take(1)).subscribe((user) => {
      if (user.loggedInUser?.id) {
        this.store.dispatch(
          DashboardActions.toggleMeasureFavorite({
            userId: user.loggedInUser.id,
            measureCode,
            isFavorite: this.isFavorite(measureCode),
          }),
        );
      }
    });
  }

  onShowFavoritesOnlyChange(): void {
    this.applyTabFilter(this.selectedTab);
  }

  toggleBenchmarks(): void {
    this.showBenchmarks = !this.showBenchmarks;
  }

  selectTab(tab: string): void {
    this.selectedTab = tab;

    this.measures = this.measures.map((m) => ({
      ...m,
      expanded: false,
      showChild: false,
    }));

    this.applyTabFilter(tab);
    this.closeDetails();
    this.updateGaugesFromReweighting();
  }

  applyTabFilter(tab: string): void {
    const sortBySortOrder = (a: DashboardMeasureDetailProd, b: DashboardMeasureDetailProd) =>
      (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER);

    const withResults = this.measures.filter(m => m.hasResults !== false).sort(sortBySortOrder);
    const withoutResults = this.measures.filter(m => m.hasResults === false).sort(sortBySortOrder);

    let filteredResults: DashboardMeasureDetailProd[] = [];
    let filteredNoResults: DashboardMeasureDetailProd[] = [];

    switch (tab) {
      case "mips measures":
        filteredResults = withResults.filter((measure) => measure.isMIPS);
        filteredNoResults = withoutResults.filter((measure) => measure.isMIPS);
        break;
      case "mvp measures":
        filteredResults = withResults.filter((measure) => measure.isMVP == true);
        filteredNoResults = withoutResults.filter((measure) => measure.isMVP == true);
        break;
      case "all":
      default:
        filteredResults = [...withResults];
        filteredNoResults = [...withoutResults];
        break;
    }

    this.filteredMeasures = filteredResults.filter((measure) => {
      if (
        this.filters.measureSelection.filter((id) => id !== "all").length > 0 &&
        !this.filters.measureSelection.includes(measure.name ?? "")
      ) {
        return false;
      }
      if (this.showFavoritesOnly && !this.favoriteMeasureCodesSet.has(measure.name ?? '')) {
        return false;
      }
      return true;
    });

    this.measuresWithoutResults = this.showFavoritesOnly
      ? filteredNoResults.filter((measure) => this.favoriteMeasureCodesSet.has(measure.name ?? ''))
      : filteredNoResults;
  }

  getAvailableMeasures(): DashboardMeasureDetailProd[] {
    let availablemeasures = [];
    switch (this.selectedTab.toLowerCase()) {
      case "mips measures":
        availablemeasures = this.measures.filter((measure) => measure.isMIPS);
        break;
      case "mvp measures":
        availablemeasures = this.measures.filter((measure) => measure.isMVP);
        break;
      case "all":
      default:
        availablemeasures = [...this.measures];
        break;
    }
    return availablemeasures;
  }

  getUniqueMeasuresById(measures: any[]): any[] {
    const seen = new Set();
    return measures.filter((measure) => {
      if (seen.has(measure.name)) {
        return false;
      }
      seen.add(measure.name);
      return true;
    });
  }

  getRequestFilter(): DashboardFilterRequest {
    return {
      clinician: this.filters.clinician,
      location: this.filters.location,
      measureYear: +this.filters.measureYear,
      useTinAggregation: this.isTinAggregationEnabled,
    };
  }

  applyFilters(): void {
    this.isLoading = true;

    this.store.dispatch(
      DashboardActions.saveFilterState({
        measureSelection: this.filters.measureSelection,
        clinician: this.filters.clinician,
        location: this.filters.location,
        selectGroup: this.filters.selectGroup,
        measureYear: this.filters.measureYear,
        isTinAggregationEnabled: this.isTinAggregationEnabled,
      }),
    );

    this.organizationID$
      .pipe(
        filter((orgId) => !!orgId && orgId !== ""),
        take(1),
      )
      .subscribe((orgId) => {
        this.store.dispatch(
          DashboardActions.applyFilters({
            orgId: +orgId,
            filters: this.getRequestFilter(),
          }),
        );
      });
  }

  onTinAggregationChange(): void {
    if (this.isTinAggregationEnabled && this.selectedDetailsTab === "patientData") {
      this.selectedDetailsTab = "performanceData";
      this.store.dispatch(DashboardActions.selectDetailsTab({ tab: "performanceData" }));
    }
    this.applyFilters();
  }

  getColor(
    measure: string,
    value: number,
    benchmark: number,
    isInverse: boolean,
  ): string {
    if (isInverse) {
      return this.getInverseMeasureColor(measure, value, benchmark);
    } else {
      return this.getNormalMeasureColor(measure, value, benchmark);
    }
  }

  isInverseMeasure(measure: DashboardMeasureDetailProd): boolean {
    if (measure.inverse === undefined) {
      return false;
    }
    return measure.inverse;
  }

  getNormalMeasureColor(
    measure: string,
    value: number,
    benchmark: number,
  ): string {
    if (value >= benchmark) {
      return "#0099AB";
    } else if (value < benchmark && value >= benchmark - 0.1) {
      return "#FF9800";
    } else {
      return "#BF311A";
    }
  }

  getInverseMeasureColor(
    measure: string,
    value: number,
    benchmark: number,
  ): string {
    if (value > benchmark) {
      return "#BF311A";
    } else {
      return "#0099AB";
    }
  }

  hasSmallDenominator(measure: DashboardMeasureDetailProd): boolean {
    return !(measure.effectiveCaseMinimumMet ?? true);
  }

  onSmallPracticeChange(): void {
    this.store.dispatch(
      DashboardActions.setSmallPractice({
        isSmallPractice: this.isSmallPractice,
      }),
    );
  }

  onMeasureClick(measure: DashboardMeasureDetailProd, index: number): void {
    this.store.dispatch(DashboardActions.clickOnMeasure({ measure, index }));
    if (this.selectedIndex === index && this.showMeasureDetails) {
      this.closeDetails();
      return;
    }

    this.selectedDetailsTab = "performanceData";
    this.selectedMeasure = measure;
    this.selectedIndex = index;
    this.showMeasureDetails = true;
  }

  closeDetails(): void {
    this.showMeasureDetails = false;
    this.selectedIndex = null;
  }

  onViewPatients(measureId: string): void {
    this.showMeasureDetails = false;
    this.showPatientDetails = true;
  }

  closePatientDetails(): void {
    this.showPatientDetails = false;
    this.showMeasureDetails = true;
  }

  onExportDetails(): void {
    if (this.isExportingPatients) {
      alert("Export already in progress");
      return;
    }
    this.patientDetailSection.exportPatientsCSV();
  }

  exportDetailsCSV(
    data: DashboardMeasurePatient[],
    filename = "MeasurePatients.csv",
  ) {
    const csv = this.convertDetailsToCSV(data);
    const blob = new Blob([csv], { type: "text/csv" });
    downloadBlob(blob, filename);
  }

  convertDetailsToCSV(data: DashboardMeasurePatient[]): string {
    const flatData = data.map(this.flattenPatients);
    const headers = Object.keys(flatData[0]);
    const rows = flatData.map((obj) =>
      headers
        .map((key) => `"${String(obj[key]).replace(/"/g, '""')}"`)
        .join(","),
    );
    return [headers.join(","), ...rows].join("\r\n");
  }

  flattenPatients(patient: DashboardMeasurePatient): Record<string, any> {
    return {
      patientID: patient.patientID,
      providerName: patient.providerName,
      firstName: patient.firstName,
      lastName: patient.lastName,
      measureId: patient.measureID,
      location: patient.location,
      measureName: patient.measureName,
      gender: patient.gender,
      age: patient.age,
      encounterDate: patient.encounterDate,
      den: patient.den,
      denExcl: patient.denExcl,
      num: patient.num,
      diagCode: patient.diagCode,
      diagDesc: patient.diagDesc,
      procedureCode: patient.procedureCode,
      procedureDate: patient.procedureDate,
      medication: patient.medication,
      medicationDate: patient.medicationDate,
    };
  }

  onExport(): void {
    this.exportMeasuresCSV(this.measures, "measures.csv");
  }

  exportMeasuresCSV(
    data: DashboardMeasureDetailProd[],
    filename = "measures.csv",
  ) {
    const csv = this.convertMeasuresToCSV(data);
    const blob = new Blob([csv], { type: "text/csv" });
    downloadBlob(blob, filename);
  }

  flattenMeasure(measure: DashboardMeasureDetailProd): Record<string, any> {
    return {
      name: measure.name ?? "",
      description: measure.measureDescription ?? "",
      url: measure.url ?? "",
      ipp: measure.ipp ?? "",
      numerator: measure.numerator ?? "",
      denominator: measure.denominator ?? "",
      performance: measure.performance,
      performanceYear: measure.performanceYear ?? "",
      performanceLastYear: measure.performanceLastYear ?? "",
      benchmark_national: measure.nationalBenchMark,
      benchmark_registry: measure.registryBenchMark,
      isMIPS: measure.isMIPS ?? "",
      isMVP: measure.isMVP ?? "",
      inverse: measure.inverse ?? "",
      outcome: measure.outcome ?? "",
      noBenchmark: measure.noBenchmark ?? "",
      highPriority: measure.highPriority ?? "",
      floor: measure.floor ?? "",
      caseMinimumMet: measure.effectiveCaseMinimumMet ?? "",
      completeness:
        measure.completeness !== undefined
          ? (measure.completeness * 100).toFixed(1) + "%"
          : "",
    };
  }

  convertMeasuresToCSV(data: DashboardMeasureDetailProd[]): string {
    const flatData = data.map(this.flattenMeasure);
    const headers = Object.keys(flatData[0]);
    const rows = flatData.map((obj) =>
      headers
        .map((key) => `"${String(obj[key]).replace(/"/g, '""')}"`)
        .join(","),
    );
    return [headers.join(","), ...rows].join("\r\n");
  }

  selectDetailsTab(tab: string): void {
    if (tab === "mips points data") {
      this.selectedDetailsTab = "performanceData";
    } else if (tab == "monthly trend") {
      this.selectedDetailsTab = "monthlyTrend";
    } else {
      this.selectedDetailsTab = "patientData";
    }

    this.store.dispatch(
      DashboardActions.selectDetailsTab({ tab: this.selectedDetailsTab }),
    );
  }

  get allSelected(): boolean {
    return (
      this.filters.measureSelection.filter((id) => id !== "all").length == 0
    );
  }

  toggleAllSelection(): void {
    this.filters.measureSelection = ["all"];
  }

  get allProvidersSelected(): boolean {
    return this.filters.clinician.filter((id) => id !== "all").length == 0;
  }

  get allLocationsSelected(): boolean {
    return this.filters.location.filter((id) => id !== "all").length == 0;
  }

  toggleAllProviders(): void {
    this.filters.clinician = ["all"];
  }

  toggleAllLocations(): void {
    this.filters.location = ["all"];
  }

  itemClicked(): void {
    if (this.filters.measureSelection.length == 0) {
      this.filters.measureSelection = ["all"];
    } else if (this.filters.measureSelection.length > 1) {
      this.filters.measureSelection = this.filters.measureSelection.filter(
        (id) => id !== "all",
      );
    }
  }

  clinicianClicked(): void {
    if (this.filters.clinician.length == 0) {
      this.filters.clinician = ["all"];
    } else if (this.filters.clinician.length > 1) {
      this.filters.clinician = this.filters.clinician.filter(
        (id) => id !== "all",
      );
    }
  }

  locationClicked(): void {
    if (this.filters.location.length == 0) {
      this.filters.location = ["all"];
    } else if (this.filters.location.length > 1) {
      this.filters.location = this.filters.location.filter(
        (id) => id !== "all",
      );
    }
  }

  getDisplayName(measure: DashboardMeasureDetailProd): string {
    if (measure.measureDisplayName && measure.measureDisplayName.length > 0) {
      if (measure.isMultiPerformance && !measure.isMultiPerformanceParent && measure.populationNbr != null) {
        return `${measure.measureDisplayName}-${measure.populationNbr}`;
      }
      return measure.measureDisplayName;
    } else if (measure.name) {
      return measure.name;
    } else {
      return "";
    }
  }

  showMeasure(measure: DashboardMeasureDetailProd): boolean {
    return (
      !(measure?.isMultiPerformance ?? false) ||
      ((measure?.isMultiPerformance ?? false) &&
        ((measure?.isMultiPerformanceParent ?? false) ||
          (measure?.showChild ?? false)))
    );
  }

  toggleExpanded(
    measure: DashboardMeasureDetailProd,
    event: Event,
    index: number,
  ): void {
    event.stopPropagation();
    this.store.dispatch(
      DashboardActions.toggleMeasureExpansion({ measure, index }),
    );
  }

  onViewScoringMeasure(measure: DashboardMeasureDetailProd): void {
    if (!(measure.showChild ?? false)) {
      this.store.dispatch(
        DashboardActions.toggleMeasureExpansion({
          measure,
          index: this.measures.indexOf(measure),
        }),
      );
    }
    const target = this.filteredMeasures.find((candidate) => candidate.name === measure.name) ?? measure;
    this.selectedMeasure = target;
    this.selectedIndex = this.filteredMeasures.indexOf(target);
    afterNextRender(
      () => document.getElementById('measureDetailsAnchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      { injector: this.injector },
    );
  }

  getRelatedMeasures(
    measure: DashboardMeasureDetailProd,
  ): DashboardMeasureDetailProd[] {
    if (!measure.isMultiPerformance) {
      return [measure];
    }

    const baseMeasureId = this.getBaseMeasureId(measure.name || "");

    return this.measures.filter((m) => {
      if (!m.name) return false;
      const mBaseId = this.getBaseMeasureId(m.name);
      return mBaseId === baseMeasureId;
    });
  }

  private getBaseMeasureId(measureName: string): string {
    if (!measureName) return "";

    let cleanName = measureName.replace(/_\d{4}$/g, "");

    const match = cleanName.match(/^(.+)-(\d+)$/);
    if (match) {
      const potentialBase = match[1];

      if (/^[a-zA-Z]+-\d+-\d+$/.test(cleanName)) {
        return potentialBase;
      }
    }

    return cleanName;
  }

  private createFilteredMeasures(): Observable<any[]> {
    return combineLatest([
      this.uniqueMeasuresSubject.asObservable(),
      this.measureSearchControl.valueChanges.pipe(startWith("")),
    ]).pipe(
      map(([measures, searchTerm]) => {
        if (!measures) return [];
        if (!searchTerm) return measures;

        return measures.filter((measure) =>
          measure.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      }),
    );
  }

  private createFilteredProviders(): Observable<DashboardProviderProd[]> {
    return combineLatest([
      this.providers$,
      this.clinicianSearchControl.valueChanges.pipe(startWith("")),
    ]).pipe(
      map(([providers, searchTerm]) => {
        if (!providers) return [];
        if (!searchTerm) return providers;

        return providers.filter((provider) => {
          const fullName = `${provider.lastName}, ${provider.firstName} (NPI: ${provider.npi})`;
          return fullName.toLowerCase().includes(searchTerm.toLowerCase());
        });
      }),
    );
  }

  private createFilteredLocations(): Observable<DashboardMeasureLocation[]> {
    return combineLatest([
      this.locations$,
      this.locationSearchControl.valueChanges.pipe(startWith("")),
    ]).pipe(
      map(([locations, searchTerm]) => {
        if (!locations) return [];
        if (!searchTerm) return locations;

        return locations.filter((location) =>
          location.serviceLocationName.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      }),
    );
  }

  getMeasurePatients(): DashboardMeasurePatient[] {
    return [];
  }
}
